import { useLocation } from "@solidjs/router";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import { parseTemplatePrompt } from "@storytell/rich-text/src/converters/parseTemplatePrompt";
import { createEffect, createSignal, onCleanup, onMount } from "solid-js";
import { useWire } from "~/wire";
import type { Wire } from "~/wire/definition";
import { mentionableSkills } from "~/wire/services/SkillsService";

/** Skills for `parseTemplatePrompt` — fetched via wire when applying URL/chat content (avoids nested `useSkillsCache` → `useWire` under HMR / split chunks). */
async function mentionOptsFromWire(wire: Wire) {
  const result = await wire.skills.listAll();
  if (result.isErr()) return undefined;
  const enabled = mentionableSkills(result.value);
  if (enabled.length === 0) return undefined;
  return {
    extractSkills: enabled.map((s) => ({
      id: s.id,
      name: s.name,
      displayName: s.displayName,
    })),
  };
}

/** Set when we have ?chat= or ?prompt= and will populate the prompt bar; cleared after dispatch or cleanup. Lets PromptBarContext skip clearing/loading draft so URL content is not overwritten. */
let pendingChatUrlFromParams = false;
/** When true, the chat param must not be applied; set on "clear-prompt-bar" and cleared after a short delay so New chat wins even if the effect re-runs with a stale URL. */
let skipNextApplyFromUrl = false;
let skipNextApplyTimeoutId: ReturnType<typeof setTimeout> | null = null;

export const getPendingChatUrlFromParams = (): boolean => pendingChatUrlFromParams;

/**
 * The prompt a `?chat=` / `?prompt=` link carries, or `undefined` when the link
 * carries nothing to apply.
 *
 * `location.query` is what `createPromptShareUrl` wrote, decoded once by the
 * router, so the value is the prompt as typed and is used as read. Decoding it
 * again rewrites an escape the sharer meant literally — "50%20off" becomes
 * "50 off" — and throws `URIError` on a bare '%', which would drop the whole
 * link rather than the one character.
 */
export const promptTextFromChatParam = (chatParam: string | string[] | undefined): string | undefined => {
  if (typeof chatParam !== "string") return undefined;
  const promptText = chatParam.trim();
  if (!promptText || promptText.includes("<script")) return undefined;
  return promptText;
};

/** Handles `?chat=` / `?prompt=`: inserts the text into the prompt bar and auto-submits. */
export const useChatUrlParameter = () => {
  const location = useLocation();
  const wire = useWire();

  const chatParam = location.query.chat || location.query.prompt;
  if (promptTextFromChatParam(chatParam) !== undefined) {
    pendingChatUrlFromParams = true;
  }

  onMount(() => {
    const unsubClear = subscribeToGlobalEvent("clear-prompt-bar", () => {
      skipNextApplyFromUrl = true;
      if (skipNextApplyTimeoutId) clearTimeout(skipNextApplyTimeoutId);
      skipNextApplyTimeoutId = setTimeout(() => {
        skipNextApplyFromUrl = false;
        skipNextApplyTimeoutId = null;
      }, 400);
    });
    onCleanup(() => {
      unsubClear();
      if (skipNextApplyTimeoutId) {
        clearTimeout(skipNextApplyTimeoutId);
        skipNextApplyTimeoutId = null;
      }
      skipNextApplyFromUrl = false;
    });
  });

  onMount(() => {
    const promptText = promptTextFromChatParam(chatParam);
    if (promptText === undefined) {
      return;
    }

    // Collect variable pre-fill values from URL params (e.g. ame_value=Andi from share link)
    const variableValues: Record<string, string> = {};
    const query = location.query;
    if (typeof query === "object" && query !== null) {
      for (const key of Object.keys(query)) {
        if (key.endsWith("_value")) {
          try {
            const varName = decodeURIComponent(key.slice(0, -6));
            const val = query[key];
            const str = Array.isArray(val) ? val[0] : val;
            if (typeof str === "string" && str !== "") {
              variableValues[varName] = str;
            }
          } catch {
            // ignore malformed keys
          }
        }
      }
    }

    const applyPrompt = async () => {
      const mentionOpts = await mentionOptsFromWire(wire);
      const doc = parseTemplatePrompt(promptText, mentionOpts);
      const docPayload = {
        doc,
        ...(Object.keys(variableValues).length > 0 && { variableValues }),
        autoSubmit: true,
      };

      queueMicrotask(() => {
        dispatchGlobalEvent("set-prompt-bar-doc", docPayload);
        pendingChatUrlFromParams = false;
      });
    };

    waitForChatUrlReady(
      () => wire.auth.isReady() && wire.grants.isLoaded(),
      () => void applyPrompt()
    );
    onCleanup(() => {
      pendingChatUrlFromParams = false;
    });
  });
};

/**
 * Calls `apply` once, when `ready` holds and the prompt bar has announced itself. Gives up, and
 * releases the prompt bar hold, if the prompt bar is cleared first or nothing is ready after 10s.
 * Must run under a Solid owner; disposing it cancels the wait.
 */
export const waitForChatUrlReady = (ready: () => boolean, apply: () => void) => {
  const [promptBarReady, setPromptBarReady] = createSignal(false);
  let settled = false;
  const giveUp = () => {
    settled = true;
    pendingChatUrlFromParams = false;
  };

  const timeoutId = setTimeout(giveUp, 10_000);
  onCleanup(() => clearTimeout(timeoutId));
  onCleanup(subscribeToGlobalEvent("prompt-bar-ready", () => setPromptBarReady(true)));
  // The skip flag below only covers a clear just before this mount; a New chat while waiting
  // has to cancel here, since nothing the effect tracks changes when it happens.
  onCleanup(subscribeToGlobalEvent("clear-prompt-bar", giveUp));

  createEffect(() => {
    if (settled) return;
    if (skipNextApplyFromUrl) {
      giveUp();
      return;
    }
    if (!ready() || !promptBarReady()) return;
    settled = true;
    clearTimeout(timeoutId);
    apply();
  });
};
